// 分类选择面板(CfxQI categoryRow Rtlmc)— v_category_public 单选(L1 / L2 叶子 + 不分类)。
//
// 文章 category_id 单值(可为 L1 或 L2 叶子);底层只存 uuid。
// 2026-06-05 module spec:传入 module 时只显示该模块分类(按 module 字段过滤)。

import { Spinner, cn } from '@dogdisk/shared'
import { usePublicCategories, type Module } from '../../_lib/discover-api'

type CategoryPickerPanelProps = {
  open: boolean
  selectedId: string | null
  module?: Module          // 传入时只显示该模块分类
  onClose: () => void
  onSelect: (id: string | null) => void
}

export function CategoryPickerPanel({
  open,
  selectedId,
  module,
  onClose,
  onSelect,
}: CategoryPickerPanelProps) {
  const cats = usePublicCategories(module)

  if (!open) return null

  const pick = (id: string | null) => {
    onSelect(id)
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-end bg-black/50"
      data-testid="article-category-panel"
      onClick={onClose}
    >
      <div
        className="mx-auto w-full max-w-[402px] rounded-t-3xl bg-white"
        style={{ maxHeight: '80vh', paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-2 pb-2">
          <span className="h-1 w-10 rounded-full bg-[#E0E0E0]" />
        </div>
        <p className="px-6 pb-2 text-[14px] font-semibold text-[#1A1A1A]">选择分类</p>
        {cats.isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner className="text-gray-400" />
          </div>
        ) : (
          <div className="flex max-h-[60vh] flex-col overflow-y-auto px-6 pb-4">
            <CatRow label="不分类" active={selectedId === null} onClick={() => pick(null)} />
            {(cats.data ?? []).map((l1) => (
              <div key={l1.id} className="flex flex-col">
                <CatRow label={l1.name} active={selectedId === l1.id} onClick={() => pick(l1.id)} bold />
                {l1.children.map((c) => (
                  <CatRow
                    key={c.id}
                    label={c.name}
                    active={selectedId === c.id}
                    onClick={() => pick(c.id)}
                    indent
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function CatRow({
  label,
  active,
  onClick,
  bold,
  indent,
}: {
  label: string
  active: boolean
  onClick: () => void
  bold?: boolean
  indent?: boolean
}) {
  return (
    <button
      type="button"
      data-testid="article-category-option"
      onClick={onClick}
      className={cn(
        'flex items-center justify-between rounded-lg py-2.5 text-left text-[14px] active:bg-[#F6F7F8]',
        indent ? 'pl-6 pr-3' : 'px-3',
        bold ? 'font-semibold text-[#1A1A1A]' : 'text-[#374151]',
        active && 'text-[#19caad]',
      )}
    >
      <span>{label}</span>
      {active && (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      )}
    </button>
  )
}
